// src/pages/ForgotPassword.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    title: "",
    message: "",
  });

  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  // 📱 Detect phone/tablet to apply wider card + tighter side padding
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const getFreshToken = async () => {
    if (!recaptchaRef.current) {
      throw new Error("captcha-not-ready");
    }

    const EXEC_TIMEOUT_MS = 15000;

    const execPromise = recaptchaRef.current.executeAsync();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("captcha-timeout")), EXEC_TIMEOUT_MS)
    );

    try {
      return await Promise.race([execPromise, timeoutPromise]);
    } finally {
      try {
        recaptchaRef.current.reset();
      } catch {}
    }
  };

  const closeSuccessDialog = () => {
    setSuccessDialog({
      open: false,
      title: "",
      message: "",
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (loading) return;

    setError("");

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError("Please enter your email address.");
      return;
    }

    if (!SITE_KEY_V2) {
      setError("reCAPTCHA not configured.");
      return;
    }

    setLoading(true);

    try {
      const recaptchaToken = await getFreshToken();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/auth/request-password-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: normalizedEmail,
            email_action_link: `${process.env.REACT_APP_FRONTEND_URL}/login`,
            recaptchaToken,
          }),
        }
      );

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok && data.success) {
        setSuccessDialog({
          open: true,
          title: "Reset Link Sent",
          message:
            data.message ||
            "If an account exists for this email, a password reset link has been sent.",
        });
        return;
      }

      setError(
        data.error ||
          data.message ||
          "Failed to send the password reset email. Please try again."
      );
    } catch (err) {
      console.error("❌ Password reset request error:", err);

      const message = String(err?.message || "");
      if (
        message.includes("captcha") ||
        message.includes("Timeout") ||
        message.includes("timeout")
      ) {
        setError("reCAPTCHA expired or failed. Please try again.");
      } else {
        setError("Unexpected error. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ✅ Same vertical start; small screens: slimmer padding + wider card */}
      <div
        style={{
          ...styles.outerWrap,
          ...(isSmallScreen ? styles.outerWrapCompact : null),
        }}
      >
        <div
          style={{
            ...styles.card,
            ...(isSmallScreen ? styles.cardWide : null),
          }}
        >
          <form onSubmit={handleResetPassword} style={styles.form} noValidate>
            <h2 style={styles.title}>Forgot Password</h2>

            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              disabled={loading}
            />

            <div style={{ height: 0, overflow: "hidden" }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={SITE_KEY_V2}
                size="invisible"
              />
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : null),
              }}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>

            {/* Inline validation/request errors stay inside the form. */}
            {error && <p style={styles.error}>{error}</p>}

            <p style={styles.backToLogin} onClick={() => navigate("/login")}>
              🔙 Back to Login
            </p>
          </form>
        </div>
      </div>

      {/* Successful action notification: centered inside the app. */}
      {successDialog.open && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialogCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-success-title"
          >
            <div style={styles.dialogIcon}>✓</div>

            <h3
              id="forgot-password-success-title"
              style={styles.dialogTitle}
            >
              {successDialog.title}
            </h3>

            <p style={styles.dialogMessage}>{successDialog.message}</p>

            <button
              type="button"
              onClick={closeSuccessDialog}
              style={styles.dialogButton}
              autoFocus
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  // Matches Login/Contact/FileVault vertical start
  outerWrap: {
    maxWidth: 960,
    margin: "24px auto",
    padding: "0 16px 40px",
  },
  // 📱 tighter side padding on small screens
  outerWrapCompact: {
    padding: "0 8px 40px",
  },
  // Same card metrics as Login
  card: {
    maxWidth: 440,
    margin: "0 auto",
    padding: 28,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  // 📱 allow a wider card on small screens (matches Signup/Login/Contact/Opt-In)
  cardWide: {
    maxWidth: 520,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    fontFamily: "sans-serif",
  },
  title: {
    textAlign: "center",
    fontSize: 26,
    fontWeight: "bold",
    marginBottom: 20,
    color: "#111827",
  },
  input: {
    padding: 12,
    fontSize: 16,
    borderRadius: 10,
    border: "1px solid #ccc",
    outline: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  button: {
    padding: 12,
    fontSize: 16,
    backgroundColor: "#1E2A78",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  buttonDisabled: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
  error: {
    color: "#b00020",
    fontSize: 14,
    textAlign: "center",
  },
  backToLogin: {
    marginTop: 10,
    fontSize: 16,
    textAlign: "center",
    color: "#1E2A78",
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "underline",
  },
  dialogOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    boxSizing: "border-box",
  },
  dialogCard: {
    width: "100%",
    maxWidth: 420,
    padding: "28px 24px 24px",
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.22)",
    textAlign: "center",
    boxSizing: "border-box",
  },
  dialogIcon: {
    width: 46,
    height: 46,
    margin: "0 auto 14px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ecfdf3",
    color: "#0a7c2f",
    fontSize: 24,
    fontWeight: 800,
  },
  dialogTitle: {
    margin: "0 0 10px",
    color: "#111827",
    fontSize: 22,
    fontWeight: 800,
  },
  dialogMessage: {
    margin: "0 0 22px",
    color: "#4b5563",
    fontSize: 15,
    lineHeight: 1.55,
    overflowWrap: "anywhere",
  },
  dialogButton: {
    minWidth: 120,
    padding: "11px 20px",
    fontSize: 15,
    backgroundColor: "#1E2A78",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
};

export default ForgotPassword;
